// 채팅(설계서 §9.6) — 뷰어 ↔ 에이전트 "chat" DataChannel 규약(순수 로직).
//
// 🔴 에이전트 `poc/rtc-core-rust/src/chat.rs` · Android `ChatProtocol.kt` 와 **같은 규약**이다 —
//    메시지 이름·필드·상한을 바꿀 때는 셋을 함께 바꾼다. 값이 어긋나면 "보냈는데 상대에게 안 뜬다" 가
//    되고, 화면만 보고는 어느 쪽이 틀렸는지 알 수 없다.
// 브라우저 전용 API 를 쓰지 않는다(TextEncoder·atob·btoa 는 Node 에도 있다) → Node 에서 그대로 시험한다.
//
// 메시지(JSON 텍스트, 필드 t):
//   에이전트→뷰어  hello {v, maxText, maxImage, ui}   채널이 열리면 1회 — 뷰어가 채팅 단추를 켠다
//   양방향         msg   {id, text}                    텍스트 한 건
//   양방향         put   {id, size} → data {id, seq, d} → end {id}   이미지 한 건(PNG)
//   양방향         ack   {id, ok, err?}                상대 **화면에 놓였는가**
//   양방향         cancel {id}
//
// base64·PNG 서명 검사는 클립보드 규약 모듈의 것을 **재사용**한다(같은 일을 두 번 구현하지 않는다).

import { CHUNK_RAW_BYTES, fromBase64, isPng, toBase64 } from '../clipboard/protocol';

/** DataChannel 이름(에이전트 `CHAT_CHANNEL_LABEL`). */
export const CHAT_CHANNEL_LABEL = 'chat';
/** 규약 버전(hello.v). */
export const CHAT_PROTOCOL_VERSION = 1;

/** 텍스트 한 건의 상한(UTF-8 바이트) — 에이전트 `chat::MAX_TEXT_BYTES` 와 같다. */
export const MAX_TEXT_BYTES = 8 * 1024;
/** 이미지 상한(PNG 바이트) — 클립보드와 같은 값을 쓴다. */
export const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
/** 이미지 픽셀 상한(압축 폭탄 방지). */
export const MAX_IMAGE_PIXELS = 40_000_000;

export const CHAT_MSG = {
  HELLO: 'hello',
  TEXT: 'msg',
  PUT: 'put',
  DATA: 'data',
  END: 'end',
  ACK: 'ack',
  CANCEL: 'cancel',
} as const;

export const CHAT_ERR = {
  TOO_LARGE: 'too_large',
  NO_UI: 'no_ui',
  SECURE_DESKTOP: 'secure_desktop',
  BUSY: 'busy',
  PROTOCOL: 'protocol',
  FAILED: 'failed',
} as const;

/** 오류 코드 → 사용자 문구. */
export const CHAT_ERR_MESSAGE: Readonly<Record<string, string>> = {
  [CHAT_ERR.TOO_LARGE]: '너무 큽니다. 글은 8KB, 이미지는 10MB 까지 보낼 수 있습니다.',
  [CHAT_ERR.NO_UI]: '상대 기기에 채팅을 표시할 화면이 없습니다.',
  [CHAT_ERR.SECURE_DESKTOP]:
    '상대 화면이 잠금·로그인 화면이거나 관리자 권한 창이 떠 있어 전달할 수 없습니다.',
  [CHAT_ERR.BUSY]: '앞의 전송이 아직 진행 중입니다. 잠시 뒤 다시 보내세요.',
  [CHAT_ERR.PROTOCOL]: '보낸 내용의 형식이 잘못됐습니다.',
  [CHAT_ERR.FAILED]: '전달하지 못했습니다.',
};

/** 채팅 실패 — `code` 는 규약 코드, 문구는 `CHAT_ERR_MESSAGE` 에서 찾는다. */
export class ChatError extends Error {
  readonly code: string;

  constructor(code: string, message?: string) {
    super(message ?? CHAT_ERR_MESSAGE[code] ?? code);
    this.code = code;
    this.name = 'ChatError';
  }
}

/**
 * 피제어측이 채팅을 표시할 수 있는 수단(hello.ui).
 * - `app`: 통합앱 창에 그린다.
 * - `none`: 표시할 화면이 없다 → 뷰어는 입력을 잠그고 사유를 보여준다(보내 놓고 아무도 못 보는 일 방지).
 */
export type ChatUi = 'app' | 'none';

export function parseUi(s: unknown): ChatUi | null {
  return s === 'app' || s === 'none' ? s : null;
}

/** 주고받을 수 있는 내용 종류(hello.kinds). 텍스트는 늘 되고, 이미지는 상대의 표시 수단에 따라 갈린다. */
export type ChatKind = 'text' | 'image';

export function parseKind(s: unknown): ChatKind | null {
  return s === 'text' || s === 'image' ? s : null;
}

/** 에이전트가 채널을 열며 보내는 능력 통지. */
export interface ChatCaps {
  version: number;
  /** 상대가 받을 수 있는 종류 — 이미지가 없으면 뷰어가 붙여넣기를 **아예 막는다**. */
  kinds: readonly ChatKind[];
  maxText: number;
  maxImage: number;
  ui: ChatUi;
}

/** `hello` 파싱. 규약 위반이면 null(채팅 단추를 켜지 않는다). */
export function parseHello(v: Record<string, unknown>): ChatCaps | null {
  if (v.t !== CHAT_MSG.HELLO) {
    return null;
  }
  const ui = parseUi(v.ui);
  if (!ui) {
    return null;
  }
  // 종류 목록이 없거나 비면 **텍스트만**으로 본다(보수적 기본값 — 못 받는 것을 보내지 않는다).
  const kinds = Array.isArray(v.kinds)
    ? (v.kinds.map(parseKind).filter((k): k is ChatKind => k != null) as ChatKind[])
    : [];
  const num = (x: unknown, d: number) => (typeof x === 'number' && x > 0 ? x : d);
  return {
    version: num(v.v, 1),
    kinds: kinds.length > 0 ? kinds : ['text'],
    maxText: num(v.maxText, MAX_TEXT_BYTES),
    maxImage: num(v.maxImage, MAX_IMAGE_BYTES),
    ui,
  };
}

const utf8 = new TextEncoder();

/** 글의 UTF-8 바이트 수(상한 검사는 글자 수가 아니라 바이트로 한다 — 한글은 3바이트다). */
export function textBytes(text: string): number {
  return utf8.encode(text).length;
}

/**
 * 보내기 전에 상한을 확인한다 — 보낸 뒤 `ack` 로 거절당하는 것보다 빠르고, 이유가 정확하다.
 * 빈 글도 여기서 막는다(에이전트도 거절하지만, 왕복 없이 입력칸에서 알리는 편이 낫다).
 */
export function checkText(text: string, caps: Pick<ChatCaps, 'maxText'>): void {
  if (text.length === 0) {
    throw new ChatError(CHAT_ERR.PROTOCOL, '보낼 글이 없습니다.');
  }
  if (textBytes(text) > caps.maxText) {
    throw new ChatError(CHAT_ERR.TOO_LARGE);
  }
}

export function checkImage(png: Uint8Array, caps: Pick<ChatCaps, 'maxImage'>): void {
  if (!isPng(png)) {
    throw new ChatError(CHAT_ERR.PROTOCOL, 'PNG 이미지만 보낼 수 있습니다.');
  }
  if (png.length === 0 || png.length > caps.maxImage) {
    throw new ChatError(CHAT_ERR.TOO_LARGE);
  }
}

export function encodeText(id: string, text: string): string {
  return JSON.stringify({ t: CHAT_MSG.TEXT, id, text });
}

export function encodeAck(id: string, code?: string): string {
  return code
    ? JSON.stringify({ t: CHAT_MSG.ACK, id, ok: false, err: code })
    : JSON.stringify({ t: CHAT_MSG.ACK, id, ok: true });
}

export function encodeCancel(id: string): string {
  return JSON.stringify({ t: CHAT_MSG.CANCEL, id });
}

/** 이미지 하나를 put + data… + end 메시지 열로 쪼갠다(조각 크기는 클립보드와 같다). */
export function encodeImageFrames(id: string, png: Uint8Array): string[] {
  const frames = [JSON.stringify({ t: CHAT_MSG.PUT, id, size: png.length })];
  for (let seq = 0, at = 0; at < png.length; seq++, at += CHUNK_RAW_BYTES) {
    frames.push(
      JSON.stringify({
        t: CHAT_MSG.DATA,
        id,
        seq,
        d: toBase64(png.subarray(at, at + CHUNK_RAW_BYTES)),
      }),
    );
  }
  frames.push(JSON.stringify({ t: CHAT_MSG.END, id }));
  return frames;
}

/** 들어온 메시지 한 건(판별 유니온). */
export type ChatIn =
  | { t: 'hello'; caps: ChatCaps }
  | { t: 'msg'; id: string; text: string }
  | { t: 'put'; id: string; size: number }
  | { t: 'data'; id: string; seq: number; d: string }
  | { t: 'end'; id: string }
  | { t: 'cancel'; id: string }
  | { t: 'ack'; id: string; ok: boolean; err?: string };

/** 전송 id 최대 길이(에이전트 `MAX_ID_LEN` 과 같다). */
const MAX_ID_LEN = 64;

function takeId(v: Record<string, unknown>): string {
  const id = typeof v.id === 'string' ? v.id : '';
  if (!id || id.length > MAX_ID_LEN) {
    throw new ChatError(CHAT_ERR.PROTOCOL, '전송 id 없음/과대');
  }
  return id;
}

/**
 * 메시지 한 줄을 파싱한다. **규약 밖 값은 예외**로 거절한다(조용히 기본값으로 떨어뜨리지 않는다 —
 * `chat.rs::parse` 와 같은 방침).
 */
export function parse(raw: string): ChatIn {
  let v: Record<string, unknown>;
  try {
    v = JSON.parse(raw) as Record<string, unknown>;
  } catch {
    throw new ChatError(CHAT_ERR.PROTOCOL, 'JSON 아님');
  }
  switch (v.t) {
    case CHAT_MSG.HELLO: {
      const caps = parseHello(v);
      if (!caps) {
        throw new ChatError(CHAT_ERR.PROTOCOL, 'hello 형식 오류');
      }
      return { t: 'hello', caps };
    }
    case CHAT_MSG.TEXT: {
      const id = takeId(v);
      const text = typeof v.text === 'string' ? v.text : null;
      if (text == null || text.length === 0) {
        throw new ChatError(CHAT_ERR.PROTOCOL, 'text 없음/빈 글');
      }
      if (textBytes(text) > MAX_TEXT_BYTES) {
        throw new ChatError(CHAT_ERR.TOO_LARGE);
      }
      return { t: 'msg', id, text };
    }
    case CHAT_MSG.PUT: {
      const id = takeId(v);
      const size = typeof v.size === 'number' ? v.size : -1;
      if (!Number.isInteger(size) || size <= 0) {
        throw new ChatError(CHAT_ERR.PROTOCOL, 'size 없음/0');
      }
      // 🔴 상한은 **조각을 받기 전에** 선언 크기로 거른다.
      if (size > MAX_IMAGE_BYTES) {
        throw new ChatError(CHAT_ERR.TOO_LARGE);
      }
      return { t: 'put', id, size };
    }
    case CHAT_MSG.DATA: {
      const id = takeId(v);
      const seq = typeof v.seq === 'number' ? v.seq : -1;
      const d = typeof v.d === 'string' ? v.d : null;
      if (!Number.isInteger(seq) || seq < 0 || d == null) {
        throw new ChatError(CHAT_ERR.PROTOCOL, 'seq/d 없음');
      }
      return { t: 'data', id, seq, d };
    }
    case CHAT_MSG.END:
      return { t: 'end', id: takeId(v) };
    case CHAT_MSG.CANCEL:
      return { t: 'cancel', id: takeId(v) };
    case CHAT_MSG.ACK: {
      const id = takeId(v);
      if (typeof v.ok !== 'boolean') {
        throw new ChatError(CHAT_ERR.PROTOCOL, 'ok 없음');
      }
      return { t: 'ack', id, ok: v.ok, err: typeof v.err === 'string' ? v.err : undefined };
    }
    default:
      throw new ChatError(CHAT_ERR.PROTOCOL, `모르는 메시지 t=${String(v.t)}`);
  }
}

/** PNG 머리(IHDR)의 폭·높이 — 풀기 전에 픽셀 상한을 확인하려고 읽는다. */
export function pngDimensions(b: Uint8Array): { width: number; height: number } | null {
  if (!isPng(b) || b.length < 24) {
    return null;
  }
  const ihdr = String.fromCharCode(b[12], b[13], b[14], b[15]);
  if (ihdr !== 'IHDR') {
    return null;
  }
  const be = (at: number) => (b[at] << 24) | (b[at + 1] << 16) | (b[at + 2] << 8) | b[at + 3];
  return { width: be(16) >>> 0, height: be(20) >>> 0 };
}

/** 받는 쪽 이미지 조립기. 조각 순서·누적 크기를 검사한다(`chat::Incoming` 과 같은 규칙). */
export class Incoming {
  readonly id: string;
  private readonly size: number;
  private readonly buf: Uint8Array;
  private filled = 0;
  private nextSeq = 0;

  constructor(id: string, size: number) {
    this.id = id;
    this.size = size;
    this.buf = new Uint8Array(size);
  }

  push(seq: number, d: string): void {
    if (seq !== this.nextSeq) {
      throw new ChatError(CHAT_ERR.PROTOCOL, `조각 순서 어긋남(기대 ${this.nextSeq} 받음 ${seq})`);
    }
    let bytes: Uint8Array;
    try {
      bytes = fromBase64(d);
    } catch {
      throw new ChatError(CHAT_ERR.PROTOCOL, 'base64 형식 오류');
    }
    if (this.filled + bytes.length > this.size) {
      throw new ChatError(CHAT_ERR.PROTOCOL, '선언 크기 초과');
    }
    this.buf.set(bytes, this.filled);
    this.filled += bytes.length;
    this.nextSeq += 1;
  }

  /** `end` — 크기·형식·픽셀 상한을 확인해 PNG 바이트로 만든다. */
  finish(): Uint8Array {
    if (this.filled !== this.size) {
      throw new ChatError(
        CHAT_ERR.PROTOCOL,
        `크기 불일치(선언 ${this.size} 받음 ${this.filled})`,
      );
    }
    if (!isPng(this.buf)) {
      throw new ChatError(CHAT_ERR.PROTOCOL, 'PNG 아님');
    }
    const dim = pngDimensions(this.buf);
    if (!dim || dim.width === 0 || dim.height === 0) {
      throw new ChatError(CHAT_ERR.PROTOCOL, 'PNG 머리 없음/빈 이미지');
    }
    if (dim.width * dim.height > MAX_IMAGE_PIXELS) {
      throw new ChatError(CHAT_ERR.TOO_LARGE, `${dim.width}x${dim.height} 픽셀 상한 초과`);
    }
    return this.buf;
  }
}
